import { useLocation } from 'react-router';

import { REDIRECT_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';

import { useBloomBasename } from './useBloomBasename';

interface GetRedirectRouteParams {
  basename: string;
  pathname: string;
  search?: string;
  to: string;
}

function getRedirectRoute({
  basename,
  pathname,
  search,
  to,
}: GetRedirectRouteParams) {
  if (!pathname) return `${to}?${REDIRECT_QUERY_KEY}=${basename}`;
  if (pathname === `${basename}login`)
    return `${to}?${REDIRECT_QUERY_KEY}=${basename}`;

  return `${to}?${REDIRECT_QUERY_KEY}=${pathname}${encodeURIComponent(search ?? '')}`;
}

type GetRouteWithRedirect = (to: string, disabled?: boolean) => string;

/**
 * Custom hook that returns a function to calculate the redirect route.
 * @returns {GetRouteWithRedirect} A function that returns the redirect route
 */
export function useRouteWithRedirect(): GetRouteWithRedirect {
  const location = useLocation();
  const basename = useBloomBasename();

  const getRouteWithRedirect: GetRouteWithRedirect = (
    to: string,
    disabled?: boolean
  ) => {
    return disabled
      ? to
      : getRedirectRoute({
          basename,
          pathname: location.pathname,
          search: location.search,
          to,
        });
  };

  return getRouteWithRedirect;
}

interface RouteWithRedirectParams {
  basename: string;
  redirectTo: string;
  to: string;
}

/**
 * Calculates the redirect route. Compatible with actions and loaders.
 * @returns {string} the redirect route
 */
export const routeWithRedirect = ({
  basename,
  redirectTo,
  to,
}: RouteWithRedirectParams): string => {
  return `${basename}${to}?${REDIRECT_QUERY_KEY}=${basename}${encodeURIComponent(redirectTo)}`;
};
